import Box from '@mui/material/Box';

import { AsideCallout, SectionHeading } from '@/components/molecules';
import { FeatureGrid, Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { meetingWays } from '@/content/consultation';

import { scopeLink, ways, WAYS_TITLE_ID } from '../content';

/**
 * The four ways to meet, described — the band that answers the question the
 * dropdown above it can only ask.
 *
 * "How would you like to meet?" is one select with four labels in it, and a
 * label is not enough to choose on: a visitor weighing an office visit
 * against a video call wants to know whether the trip across Kampala buys
 * them anything. So the four options are explained once, in full, below the
 * form rather than inside it — a select with four helper paragraphs in it is
 * a select nobody finishes reading.
 *
 * Below and not above, which is the only ordering decision in this page. A
 * visitor who already knows they want to talk should not scroll past four
 * cards to reach the thing they came for, and the one who does not know will
 * scroll anyway.
 *
 * `FeatureGrid` rather than a grid of its own: this is a list of
 * `ContentItem`s drawn as `IconFeature`s, which is precisely what that
 * organism is, down to the two columns from `sm`. Two columns and not four,
 * because each card is a heading over two lines of prose and a narrower
 * column would set the text on five lines.
 *
 * It renders `meetingWays` from `content/consultation.ts`, which is
 * deliberately the same four, in the same order, as `meetingTypes` in
 * `schemas/enquiry.ts`. The schema still owns what the form submits; this
 * band only explains it.
 *
 * It closes on the service page rather than restating what a consultation
 * covers. Two pages carrying the same six inclusions and the same FAQ would
 * be two pages competing for one search, and the one without a form would
 * keep winning it.
 */
export function MeetingWaysBand() {
  return (
    <Section
      tone="muted"
      pattern="diagonals"
      labelledBy={WAYS_TITLE_ID}
      backdrop={{ picture: introVehicles.noah.image, placement: 'right' }}
    >
      <SectionHeading
        id={WAYS_TITLE_ID}
        eyebrow={ways.eyebrow}
        title={ways.title}
        lede={ways.lede}
      />

      <FeatureGrid items={meetingWays} columns={2} />

      {/* Held to the width the cards above it occupy on a wide screen, so the
          card reads as the end of this band rather than as a bar across it. */}
      <Box sx={{ mt: { xs: 8, md: 12 }, maxWidth: { md: '60%' } }}>
        <AsideCallout {...scopeLink} />
      </Box>
    </Section>
  );
}
